import { useCallback } from "react";
import { useLayoutExtracaoApi } from "../../../infra/geo-veles";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../presentation/hooks/store-hooks";
import { useUIService } from "../../ui";
import {
  resetCreateLayoutFormState,
  resetCreateLayoutPadraoFormState,
  resetEditLayoutFormState,
  resetExtensaoArquivoSelecionadaState,
  resetLayoutSelecionadoState,
  resetLayoutsState,
  setCreateLayoutExtracaoFormErrorsState,
  setCreateLayoutExtracaoFormTouchedState,
  setCreateLayoutFormFieldsColunasState,
  setCreateLayoutFormFieldsNomeState,
  setCreateLayoutFormOpenState,
  setCreateLayoutPadraoExtracaoFormErrorsState,
  setCreateLayoutPadraoExtracaoFormTouchedState,
  setCreateLayoutPadraoFormNomeState,
  setEditLayoutExtracaoFormErrorsState,
  setEditLayoutExtracaoFormTouchedState,
  setEditLayoutFormFieldsColunasState,
  setEditLayoutFormFieldsIdVelesGeoLayoutExtracaoState,
  setEditLayoutFormFieldsNomeState,
  setEditLayoutFormOpenState,
  setExtensaoArquivoSelecionadaState,
  setLayoutSelecionadoState,
  setLayoutsState,
  setOpenCreateLayoutPadraoFormState,
  setOpenDeleteLayoutState,
  validateCreateLayoutExtracaoForm,
  validateCreateLayoutPadraoExtracaoForm,
  validateEditLayoutExtracaoForm,
} from "../../../application/states/geo-veles";
import { useNavigationService } from "../../navigation";
import {
  IColunaExtracao,
  IExtensaoArquivo,
  ILayoutExtracaoTable,
} from "../../../application/models";
import { velesGeoColunasLayoutPadrao } from "../../../presentation/assets/helpers";

export const useLayoutExtracaoService = () => {
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const { createLayoutForm, editLayoutForm, createLayoutPadraoForm, layoutSelecionado } =
    useAppSelector((state) => state.layoutExtracao);
  const { currentUser } = useAppSelector((state) => state.user);
  // Infra
  const {
    createLayoutApi,
    deleteLayoutApi,
    findLayoutsByIdUsuarioApi,
    updateLayoutApi,
  } = useLayoutExtracaoApi();
  // Service
  const { setAlert, setLoading } = useUIService();
  const { navigateTo } = useNavigationService();

  const createLayout = useCallback(async () => {
    if (currentUser) {
      const loadingId = Math.random().toString();

      try {
        const errors = validateCreateLayoutExtracaoForm(
          createLayoutForm.fields
        );

        if (errors.length > 0) {
          dispatch(
            setCreateLayoutExtracaoFormErrorsState({
              errors: errors,
            })
          );
          dispatch(setCreateLayoutExtracaoFormTouchedState(true));
        } else {
          setLoading({
            id: loadingId,
            loading: true,
            message: "Criando layout...",
          });

          await createLayoutApi({
            Colunas: createLayoutForm.fields.Colunas,
            IdUsuario: currentUser.IdUsuario,
            Nome: createLayoutForm.fields.Nome,
          });

          dispatch(resetCreateLayoutFormState());
          dispatch(setCreateLayoutFormOpenState(false));

          const newLoadingId = Math.random().toString();
          try {
            setLoading({
              id: newLoadingId,
              loading: true,
              message: "Atualizando layouts...",
            });
            const findLayoutsByIdUsuario = await findLayoutsByIdUsuarioApi({
              IdUsuario: currentUser.IdUsuario,
            });

            dispatch(setLayoutsState(findLayoutsByIdUsuario));
          } catch (error: any) {
            setAlert({
              message: error.response.data.error || error.response.data.message,
              type: "error",
            });
          } finally {
            setLoading({
              id: newLoadingId,
              loading: false,
            });
          }
          navigateTo({
            route: "/geo-veles/extracoes/download",
          });
          setAlert({
            message: "Layout criado com sucesso!",
            type: "success",
          });
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: "error",
        });
      } finally {
        setLoading({
          id: loadingId,
          loading: false,
        });
      }
    }
  }, [
    createLayoutApi,
    createLayoutForm.fields,
    currentUser,
    dispatch,
    findLayoutsByIdUsuarioApi,
    navigateTo,
    setAlert,
    setLoading,
  ]);

  const deleteLayout = useCallback(
    async (IdVelesGeoLayoutExtracao: number) => {
      if (currentUser) {
        try {
          await deleteLayoutApi({ IdVelesGeoLayoutExtracao });

          const loadingId = Math.random().toString();
          try {
            setLoading({
              id: loadingId,
              loading: true,
              message: "Atualizando layouts disponíveis...",
            });
            const findLayoutsByIdUsuario = await findLayoutsByIdUsuarioApi({
              IdUsuario: currentUser.IdUsuario,
            });

            dispatch(setLayoutsState(findLayoutsByIdUsuario));
          } catch (error: any) {
            setAlert({
              message: error.response.data.error || error.response.data.message,
              type: "error",
            });
          } finally {
            setLoading({
              id: loadingId,
              loading: false,
            });
          }

          setAlert({
            message: "Layout excluído com sucesso",
            type: "success",
          });
        } catch (error: any) {
          setAlert({
            message: error.response.data.error || error.response.data.message,
            type: "error",
          });
        }
      }
    },
    [
      currentUser,
      deleteLayoutApi,
      dispatch,
      findLayoutsByIdUsuarioApi,
      setAlert,
      setLoading,
    ]
  );

  const findLayouts = useCallback(async () => {
    if (currentUser) {
      const loadingId = Math.random().toString();

      try {
        const findLayoutsByIdUsuario = await findLayoutsByIdUsuarioApi({
          IdUsuario: currentUser.IdUsuario,
        });

        dispatch(setLayoutsState(findLayoutsByIdUsuario));
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: "error",
        });
      } finally {
        setLoading({
          id: loadingId,
          loading: false,
        });
      }
    }
  }, [currentUser, dispatch, findLayoutsByIdUsuarioApi, setAlert, setLoading]);

  const updateLayout = useCallback(async () => {
    if (currentUser) {
      try {
        const errors = validateEditLayoutExtracaoForm(editLayoutForm.fields);
        //const layoutAtualizado: ILayoutExtracaoTable = editLayoutForm.fields.Colunas

        const updatedLayoutSelecionado: ILayoutExtracaoTable = {
          IdVelesGeoLayoutExtracao: editLayoutForm.fields.IdVelesGeoLayoutExtracao,
          Nome: editLayoutForm.fields.Nome,
          Colunas: editLayoutForm.fields.Colunas,
          // Mantenha ou defina os valores para as propriedades adicionais
          IdUsuario: currentUser?.IdUsuario || 0, // Use o valor atual ou um padrão
          LoginUsuario: currentUser?.LoginUsuario || '', 
          NomeUsuario: currentUser?.Nome || '',
          Inclusao: layoutSelecionado?.Inclusao || new Date(),
          Atualizacao:layoutSelecionado?.Atualizacao || new Date() // Defina a data atual como Atualizacao
      };

        if (errors.length > 0) {
          dispatch(
            setEditLayoutExtracaoFormErrorsState({
              errors: errors,
            })
          );
          dispatch(setEditLayoutExtracaoFormTouchedState(true));
        } else {
          await updateLayoutApi({
            Colunas: editLayoutForm.fields.Colunas,
            IdVelesGeoLayoutExtracao:
              editLayoutForm.fields.IdVelesGeoLayoutExtracao,
            Nome: editLayoutForm.fields.Nome,
          });

          const newLoadingId = Math.random().toString();
          try {
            setLoading({
              id: newLoadingId,
              loading: true,
              message: "Atualizando layouts disponíveis...",
            });
            const findLayoutsByIdUsuario = await findLayoutsByIdUsuarioApi({
              IdUsuario: currentUser.IdUsuario,
            });

            dispatch(setLayoutsState(findLayoutsByIdUsuario));
            dispatch(setLayoutSelecionadoState(updatedLayoutSelecionado));

          } catch (error: any) {
            setAlert({
              message: error.response.data.error || error.response.data.message,
              type: "error",
            });
          } finally {
            setLoading({
              id: newLoadingId,
              loading: false,
            });
          }

          dispatch(resetEditLayoutFormState());
          dispatch(setEditLayoutFormOpenState(false));
          navigateTo({
            route: "/geo-veles/extracoes/download",
          });
          setAlert({
            message: "Layout atualizado com sucesso",
            type: "success",
          });
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: "error",
        });
      }
    }
  }, [
    currentUser,
    dispatch,
    editLayoutForm.fields,
    findLayoutsByIdUsuarioApi,
    navigateTo,
    setAlert,
    setLoading,
    updateLayoutApi,
  ]);

  const resetLayouts = useCallback(() => {
    dispatch(resetLayoutsState());
  }, [dispatch]);

  const setLayoutSelecionado = useCallback(
    (layout: ILayoutExtracaoTable) => {
      dispatch(setLayoutSelecionadoState(layout));
    },
    [dispatch]
  );

  const resetLayoutSelecionado = useCallback(() => {
    dispatch(resetLayoutSelecionadoState());
  }, [dispatch]);

  const setCreateLayoutFormFieldsNome = useCallback(
    (nome: string) => {
      dispatch(setCreateLayoutFormFieldsNomeState(nome));
    },
    [dispatch]
  );

  const setCreateLayoutFormFieldsColunas = useCallback(
    (colunas: IColunaExtracao[]) => {
      dispatch(setCreateLayoutFormFieldsColunasState(colunas));
    },
    [dispatch]
  );

  const resetCreateLayoutForm = useCallback(() => {
    dispatch(resetCreateLayoutFormState());
  }, [dispatch]);

  const setEditLayoutFormFieldsIdVelesGeoLayoutExtracao = useCallback(
    (idVelesGeoLayoutExtracao: number) => {
      dispatch(
        setEditLayoutFormFieldsIdVelesGeoLayoutExtracaoState(
          idVelesGeoLayoutExtracao
        )
      );
    },
    [dispatch]
  );

  const setEditLayoutFormFieldsNome = useCallback(
    (nome: string) => {
      dispatch(setEditLayoutFormFieldsNomeState(nome));
    },
    [dispatch]
  );

  const setEditLayoutFormFieldsColunas = useCallback(
    (colunas: IColunaExtracao[]) => {
      dispatch(setEditLayoutFormFieldsColunasState(colunas));
    },
    [dispatch]
  );

  const resetEditLayoutForm = useCallback(() => {
    dispatch(resetEditLayoutFormState());
  }, [dispatch]);

  const setExtensaoArquivoSelecionada = useCallback(
    (extensao: IExtensaoArquivo) => {
      dispatch(setExtensaoArquivoSelecionadaState(extensao));
    },
    [dispatch]
  );

  const resetExtensaoArquivoSelecionada = useCallback(() => {
    dispatch(resetExtensaoArquivoSelecionadaState());
  }, [dispatch]);

  const setCreateLayoutFormOpen = useCallback(
    (open: boolean) => {
      dispatch(setCreateLayoutFormOpenState(open));
    },
    [dispatch]
  );

  const setEditLayoutFormOpen = useCallback(
    (open: boolean) => {
      dispatch(setEditLayoutFormOpenState(open));
    },
    [dispatch]
  );

  const setOpenDeleteLayout = useCallback(
    (open: boolean) => {
      dispatch(setOpenDeleteLayoutState(open));
    },
    [dispatch]
  );

  const setCreateLayoutPadraoFormNome = useCallback(
    (nome: string) => {
      dispatch(setCreateLayoutPadraoFormNomeState(nome));
    },
    [dispatch]
  );

  const resetCreateLayoutPadraoForm = useCallback(() => {
    dispatch(resetCreateLayoutPadraoFormState());
  }, [dispatch]);

  const createLayoutPadrao = useCallback(async () => {
    if (currentUser) {
      const loadingId = Math.random().toString();

      try {
        const errors = validateCreateLayoutPadraoExtracaoForm(
          createLayoutPadraoForm.fields
        );

        if (errors.length > 0) {
          dispatch(
            setCreateLayoutPadraoExtracaoFormErrorsState({
              errors: errors,
            })
          );
          dispatch(setCreateLayoutPadraoExtracaoFormTouchedState(true));
        } else {
          setLoading({
            id: loadingId,
            loading: true,
            message: "Criando layout...",
          });

          await createLayoutApi({
            Colunas: velesGeoColunasLayoutPadrao,
            IdUsuario: currentUser.IdUsuario,
            Nome: createLayoutPadraoForm.fields.Nome,
          });

          dispatch(resetCreateLayoutPadraoFormState());
          dispatch(setOpenCreateLayoutPadraoFormState(false));

          const newLoadingId = Math.random().toString();
          try {
            setLoading({
              id: newLoadingId,
              loading: true,
              message: "Atualizando layouts...",
            });
            const findLayoutsByIdUsuario = await findLayoutsByIdUsuarioApi({
              IdUsuario: currentUser.IdUsuario,
            });

            dispatch(setLayoutsState(findLayoutsByIdUsuario));
          } catch (error: any) {
            setAlert({
              message: error.response.data.error || error.response.data.message,
              type: "error",
            });
          } finally {
            setLoading({
              id: newLoadingId,
              loading: false,
            });
          }

          navigateTo({
            route: "/geo-veles/extracoes/download",
          });
          setAlert({
            message: "Layout criado com sucesso!",
            type: "success",
          });
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: "error",
        });
      } finally {
        setLoading({
          id: loadingId,
          loading: false,
        });
      }
    }
  }, [
    createLayoutApi,
    createLayoutPadraoForm.fields,
    currentUser,
    dispatch,
    findLayoutsByIdUsuarioApi,
    navigateTo,
    setAlert,
    setLoading,
  ]);

  const setOpenCreateLayoutPadraoForm = useCallback(
    (open: boolean) => {
      dispatch(setOpenCreateLayoutPadraoFormState(open));
    },
    [dispatch]
  );

  return {
    createLayout,
    deleteLayout,
    findLayouts,
    updateLayout,
    resetLayouts,
    setLayoutSelecionado,
    resetLayoutSelecionado,
    setCreateLayoutFormFieldsNome,
    setCreateLayoutFormFieldsColunas,
    resetCreateLayoutForm,
    setEditLayoutFormFieldsIdVelesGeoLayoutExtracao,
    setEditLayoutFormFieldsNome,
    setEditLayoutFormFieldsColunas,
    resetEditLayoutForm,
    setExtensaoArquivoSelecionada,
    resetExtensaoArquivoSelecionada,
    setCreateLayoutFormOpen,
    setEditLayoutFormOpen,
    setOpenDeleteLayout,
    setCreateLayoutPadraoFormNome,
    resetCreateLayoutPadraoForm,
    createLayoutPadrao,
    setOpenCreateLayoutPadraoForm,
  };
};
